import { Box } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Loader } from "../../../common/components/loader";
import DashboardLayout from "../../../common/layouts/dashboard";
import { useAppDispatch } from "../../../core/hooks/useStore";
import { RootState } from "../../../core/store/store";
import { homeSlice } from "../../home";
import { NichePigCreateDialog } from "./niche-pig-create-dialog";
import NichePigDesc from "./niche-pig-desc";
import NichePigHeader from "./niche-pig-header";
import NichePigLoading from "./niche-pig-loading";
import NichePigLocked from "./niche-pig-locked";
import toolsSlice from "./niche-pig.slice";
import NichePigResults from "./results/results";
import useTool from "./use-niche-pig";

const NichePig = () => {
  const dispatch = useAppDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const toolsState = useSelector((state: RootState) => state.nichePig);
  const navigate = useNavigate();
  const { resultId } = useParams<{ resultId?: string }>();
  const hasAccess = auth.tools?.nicheTool;
  const loadingRef = useRef(false);

  const tool = useTool();

  useEffect(() => {
    console.log("NichePig useEffect running", { resultId, hasAccess, loadingRef: loadingRef.current });
    
    dispatch(homeSlice.actions.setActiveMenu("niche-tool"));
    dispatch(toolsSlice.actions.setType("NICHE"));
    
    if (!hasAccess) {
      navigate("/tools");
      return;
    }

    if (resultId && !loadingRef.current) {
      loadingRef.current = true;
      console.log("Starting to load result:", resultId);
      dispatch(toolsSlice.actions.setStatus("loading"));
      
      tool.getJob(resultId).then(result => {
        console.log("Loaded result:", result);
        if (result && result.finishedAt) {
          console.log("Setting data and status to created");
          dispatch(toolsSlice.actions.setData(result));
          dispatch(toolsSlice.actions.setStatus("created"));
        } else {
          console.log("No valid result, redirecting to main page");
          dispatch(toolsSlice.actions.setStatus("initialized"));
          navigate("/tools/niche-tool");
        }
        loadingRef.current = false;
      }).catch(error => {
        console.error("Failed to load result:", error);
        dispatch(toolsSlice.actions.setStatus("initialized"));
        navigate("/tools/niche-tool");
        loadingRef.current = false;
      });
    } else if (!resultId) {
      dispatch(toolsSlice.actions.setStatus("initialized"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultId, hasAccess]);

  const NichePigLoader = () => {
    return <Loader />;
  };

  const handleOpen = () => {
    dispatch(toolsSlice.actions.setCreateDialogOpened(true));
  };

  const handleClose = () => {
    dispatch(toolsSlice.actions.setCreateDialogOpened(false));
  };

  console.log("NichePig render - status:", toolsState.tool.status, "data:", toolsState.tool.data);

  return (
    <DashboardLayout active={"niche-pig"} name="Niche Pig Pro">
      <Box>
        {toolsState.tool.status === "loading" && <NichePigLoader />}
        {toolsState.tool.status === "initialized" && (
          <>
            <NichePigHeader />
            <NichePigDesc />
            <NichePigCreateDialog
              onClose={handleClose}
              open={toolsState.tool.createDialogOpened}
              defaultNiche={toolsState.tool.defaultSearch}
            />
          </>
        )}
        {toolsState.tool.status === "unauthorized" && <NichePigLocked />}
        {toolsState.tool.status === "creating" && (
          <>
            <NichePigHeader />
            <NichePigLoading />
          </>
        )}
        {toolsState.tool.status === "created" && (
          <>
            <NichePigHeader />
            <NichePigResults />
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default NichePig;
